import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageIcon from "@mui/icons-material/Image";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FacebookIcon from "@mui/icons-material/Facebook";
import PublishIcon from "@mui/icons-material/Publish";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
    Alert,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import CustomSnackbar from "../components/CustomSnackbar";
import ConfirmDialog from "../components/ConfirmDialog";
import BedrockIdeaService from "../setting/bedrock_idea/BedrockIdeaService";
import AdminJigsawService from "./AdminJigsawService";
import AdminStoryService from "../story/AdminStoryService";
import FacebookTestService from "../system/word-wheel-social/FacebookTestService";
import AdminJigsawPlay from "./play/AdminJigsawPlay";
import { isJigsawTemplateEnabled } from "./AdminJigsawTemplateService";
import {
    JIGSAW_DIFFICULTY_LABELS,
    JIGSAW_DIFFICULTY_ORDER,
    resolveJigsawTemplateTileSize,
} from "../../pages/puzzles/jigsaw/jigsawBrowseUtils";

const DIFF_LABEL = { ...JIGSAW_DIFFICULTY_LABELS };

const HIDE_DISABLED_STORAGE_KEY = "admin.jigsaw.detail.hideDisabled";
const JIGSAW_STATS_PATH = "/admin/jigsaw/stats";
const JIGSAW_BROWSE_PATH = "/admin/jigsaw/browse";

function jigsawDetailBack(location) {
    const from = location?.state?.from;
    if (typeof from === "string" && from.startsWith(JIGSAW_STATS_PATH)) {
        return { path: JIGSAW_STATS_PATH, label: "Back to Stats" };
    }
    return { path: JIGSAW_BROWSE_PATH, label: "Back to Browse" };
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 8;
const ZOOM_STEP = 0.25;

function clampZoom(value) {
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(value * 100) / 100));
}

function originalImageDownloadName(imageUrl, alt) {
    try {
        const path = new URL(imageUrl, window.location.origin).pathname;
        const base = decodeURIComponent(path.split("/").pop() || "");
        if (base && /\.[a-z0-9]{2,5}$/i.test(base)) return base;
    } catch {
        /* ignore */
    }
    const stem = String(alt || "jigsaw-original")
        .trim()
        .replace(/[^\w\-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 80);
    return `${stem || "jigsaw-original"}.jpg`;
}

async function downloadOriginalImage(imageUrl, alt) {
    if (!imageUrl) return;
    const fileName = originalImageDownloadName(imageUrl, alt);
    const response = await fetch(imageUrl);
    if (!response.ok) throw new Error("Download failed");
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = fileName;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } finally {
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    }
}

function OriginalImageViewer({ imageUrl, alt }) {
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const dragRef = useRef(null);
    const viewportRef = useRef(null);
    const wheelHandlerRef = useRef(null);
    const attachedWheelRef = useRef(null);

    useEffect(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
        setDragging(false);
        dragRef.current = null;
    }, [imageUrl]);

    wheelHandlerRef.current = (event) => {
        event.preventDefault();
        const direction = event.deltaY < 0 ? 1 : -1;
        setZoom((current) => clampZoom(current + direction * ZOOM_STEP));
    };

    const setViewportNode = useCallback((node) => {
        if (viewportRef.current && attachedWheelRef.current) {
            viewportRef.current.removeEventListener("wheel", attachedWheelRef.current);
        }
        viewportRef.current = node;
        if (node) {
            const handler = (event) => {
                wheelHandlerRef.current?.(event);
            };
            attachedWheelRef.current = handler;
            node.addEventListener("wheel", handler, { passive: false });
        } else {
            attachedWheelRef.current = null;
        }
    }, []);

    const handlePointerDown = (event) => {
        if (event.button !== 0) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            panX: pan.x,
            panY: pan.y,
        };
        setDragging(true);
    };

    const handlePointerMove = (event) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        setPan({
            x: drag.panX + (event.clientX - drag.startX),
            y: drag.panY + (event.clientY - drag.startY),
        });
    };

    const endDrag = (event) => {
        if (dragRef.current?.pointerId === event.pointerId) {
            dragRef.current = null;
            setDragging(false);
        }
    };

    const resetView = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    const handleDownload = async () => {
        if (!imageUrl || downloading) return;
        setDownloading(true);
        try {
            await downloadOriginalImage(imageUrl, alt);
        } catch {
            // CORS or network failure: open the image so the browser can save it.
            window.open(imageUrl, "_blank", "noopener,noreferrer");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                    Original image
                </Typography>
                <Tooltip title="Zoom out">
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Zoom out"
                            disabled={zoom <= ZOOM_MIN}
                            onClick={() => setZoom((current) => clampZoom(current - ZOOM_STEP))}
                            sx={{ width: 44, height: 44 }}
                        >
                            <ZoomOutIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Typography variant="caption" sx={{ minWidth: 44, textAlign: "center" }}>
                    {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zoom in">
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Zoom in"
                            disabled={zoom >= ZOOM_MAX}
                            onClick={() => setZoom((current) => clampZoom(current + ZOOM_STEP))}
                            sx={{ width: 44, height: 44 }}
                        >
                            <ZoomInIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Reset zoom">
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Reset zoom"
                            disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                            onClick={resetView}
                            sx={{ width: 44, height: 44 }}
                        >
                            <RestartAltIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Download original image">
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Download original image"
                            disabled={!imageUrl || downloading}
                            onClick={() => {
                                void handleDownload();
                            }}
                            sx={{ width: 44, height: 44 }}
                        >
                            {downloading ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <DownloadIcon fontSize="small" />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
            <Box
                ref={setViewportNode}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onDoubleClick={resetView}
                sx={{
                    width: "100%",
                    height: { xs: "70vh", md: "80vh" },
                    minHeight: 280,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "grey.900",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: dragging ? "grabbing" : "grab",
                    touchAction: "none",
                    userSelect: "none",
                }}
            >
                <Box
                    component="img"
                    src={imageUrl}
                    alt={alt}
                    draggable={false}
                    sx={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transformOrigin: "center center",
                        pointerEvents: "none",
                    }}
                />
            </Box>
        </Box>
    );
}

function loadHideDisabledPreference() {
    try {
        const v = localStorage.getItem(HIDE_DISABLED_STORAGE_KEY);
        if (v === "false") return false;
    } catch {}
    return true;
}

function getStoryTitle(story) {
    if (!story) return "";
    const en = story.english?.title ?? story.en?.title ?? "";
    const fr = story.french?.title ?? story.fr?.title ?? "";
    return en || fr || "";
}

function getImageUrl(story) {
    if (!story) return null;
    return story.imageUrl ?? story.imagePreviewUrl ?? story.imageIcon400Url ?? null;
}

function getOriginalImageUrl(story) {
    if (!story) return null;
    const url = story.imageUrl ?? story.image_url ?? null;
    return typeof url === "string" && url.trim() ? url.trim() : null;
}

function facebookCaption(title, storyId) {
    const name = title && title !== "—" ? title : "Jigsaw puzzle";
    const play = `https://www.puzzleinteract.com/puzzle/play-jigsaw/${encodeURIComponent(storyId || "")}`;
    return `${name}\n\n🧩 Jigsaw puzzle: ${play}\n\n@puzzle_interact`;
}

function getTemplatePreviewUrl(tpl) {
    if (!tpl) return null;
    const url = tpl.previewImageUrl ?? tpl.preview_image_url ?? tpl.imageUrl ?? tpl.image_url ?? null;
    return typeof url === "string" && url.trim() ? url.trim() : null;
}

export default function AdminJigsawDetailPage() {
    const { storyId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const detailBack = jigsawDetailBack(location);
    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null);
    const [error, setError] = useState(null);
    const [previewGenerating, setPreviewGenerating] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [previewResult, setPreviewResult] = useState(null);
    const [imageBust, setImageBust] = useState(0);
    const [regenDialogOpen, setRegenDialogOpen] = useState(false);
    const [cardPreview, setCardPreview] = useState({ open: false, url: null, title: "" });
    const [ensureSubmitting, setEnsureSubmitting] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [disabling, setDisabling] = useState(false);
    const [disableDialogOpen, setDisableDialogOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const [hideDisabled, setHideDisabled] = useState(loadHideDisabledPreference);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [savingFree, setSavingFree] = useState(false);
    const [bedrockIdeaTitle, setBedrockIdeaTitle] = useState("");
    const [facebookPreviewOpen, setFacebookPreviewOpen] = useState(false);
    const [facebookPosting, setFacebookPosting] = useState(false);

    const fetchGroup = useCallback(async (opts) => {
        if (!storyId) return;
        const silent = opts?.silent === true;
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        const result = await AdminJigsawService.getGroupByStoryId(storyId);
        if (result.success) {
            setGroup(result.data);
        } else if (!silent) {
            setError(result.error || "Failed to load");
        }
        if (!silent) {
            setLoading(false);
        }
    }, [storyId]);

    useEffect(() => {
        fetchGroup();
    }, [fetchGroup]);

    const handleConfirmRegenImage = useCallback(async (id) => {
        if (!id || previewGenerating) return;
        setPreviewGenerating(true);
        setPreviewResult(null);
        try {
            const result = await AdminStoryService.regenImage(id);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Regenerate image failed",
                });
                return;
            }
            setImageBust(Date.now());
            const preview = await AdminJigsawService.genPreview(id);
            if (!preview.success) {
                setPreviewResult({ error: preview.error || "Image replaced. Jigsaw preview refresh failed." });
            } else {
                const items = [...(Array.isArray(preview.data?.items) ? preview.data.items : [])];
                const diffOrder = JIGSAW_DIFFICULTY_ORDER;
                items.sort((a, b) => {
                    const ts = (a.tileSize ?? 0) - (b.tileSize ?? 0);
                    if (ts !== 0) return ts;
                    const da = diffOrder[String(a.difficultyLevel || "").toUpperCase()] ?? 99;
                    const db = diffOrder[String(b.difficultyLevel || "").toUpperCase()] ?? 99;
                    return da - db;
                });
                setPreviewResult({ items });
            }
            setPreviewDialogOpen(true);
            await fetchGroup({ silent: true });
        } catch (e) {
            setSnackbar({
                open: true,
                severity: "error",
                message: e?.message || "Regenerate image failed",
            });
        } finally {
            setPreviewGenerating(false);
        }
    }, [previewGenerating, fetchGroup]);

    const closePreviewDialog = useCallback(() => {
        setPreviewDialogOpen(false);
    }, []);

    const openCardPreview = useCallback((tpl) => {
        const name = tpl?.templateName || tpl?.name || "Template";
        setCardPreview({
            open: true,
            url: getTemplatePreviewUrl(tpl),
            title: name,
        });
    }, []);

    const closeCardPreview = useCallback(() => {
        setCardPreview({ open: false, url: null, title: "" });
    }, []);

    const handleEnsureTemplates = useCallback(async () => {
        if (!storyId) return;
        setEnsureSubmitting(true);
        try {
            const result = await AdminJigsawService.ensureStoryTemplates(storyId);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Ensure failed",
                });
                return;
            }
            const created = result.data?.templatesCreated ?? 0;
            setSnackbar({
                open: true,
                severity: created > 0 ? "success" : "info",
                message:
                    created > 0
                        ? `Created ${created} missing jigsaw row${created !== 1 ? "s" : ""}.`
                        : "No missing templates — nothing to add.",
            });
            if (created > 0) {
                fetchGroup();
            }
        } catch (e) {
            setSnackbar({
                open: true,
                severity: "error",
                message: e?.message || "Ensure failed",
            });
        } finally {
            setEnsureSubmitting(false);
        }
    }, [storyId, fetchGroup]);

    const handleDeleteClick = useCallback(() => {
        if (!storyId) return;
        setConfirmOpen(true);
    }, [storyId]);

    const handleDelete = useCallback(async (id) => {
        if (!id) return;
        setDeleting(true);
        const result = await AdminJigsawService.removeByStoryId(id);
        setDeleting(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Delete failed" });
            return;
        }
        navigate(detailBack.path);
    }, [navigate, detailBack.path]);

    const story = group?.story ?? null;
    const bedrockIdeaId = story?.bedrockIdeaId ?? story?.bedrock_idea_id ?? null;

    useEffect(() => {
        if (!bedrockIdeaId) {
            setBedrockIdeaTitle("");
            return undefined;
        }
        let cancelled = false;
        (async () => {
            const result = await BedrockIdeaService.getIdea(bedrockIdeaId);
            if (cancelled) return;
            const title = result.success ? String(result.data?.title || "").trim() : "";
            setBedrockIdeaTitle(title);
        })();
        return () => {
            cancelled = true;
        };
    }, [bedrockIdeaId]);

    const rawTemplates = group?.templates ?? [];
    const sortedTemplates = useMemo(
        () =>
            [...rawTemplates].sort((a, b) => {
                const ta = resolveJigsawTemplateTileSize(a);
                const tb = resolveJigsawTemplateTileSize(b);
                if (ta !== tb) return ta - tb;
                const da = JIGSAW_DIFFICULTY_ORDER[String(a.difficultyLevel || "").toUpperCase()] ?? 99;
                const db = JIGSAW_DIFFICULTY_ORDER[String(b.difficultyLevel || "").toUpperCase()] ?? 99;
                return da - db;
            }),
        [rawTemplates],
    );
    const disabledTemplateCount = useMemo(
        () => sortedTemplates.filter((tpl) => !isJigsawTemplateEnabled(tpl)).length,
        [sortedTemplates],
    );
    const templates = useMemo(() => {
        if (!hideDisabled) return sortedTemplates;
        return sortedTemplates.filter((tpl) => isJigsawTemplateEnabled(tpl));
    }, [sortedTemplates, hideDisabled]);

    const handleHideDisabledChange = useCallback((checked) => {
        setHideDisabled(checked);
        try {
            localStorage.setItem(HIDE_DISABLED_STORAGE_KEY, checked ? "true" : "false");
        } catch {}
    }, []);

    const handleFreeChange = useCallback(async (checked) => {
        if (!storyId) return;
        setSavingFree(true);
        const result = await AdminJigsawService.setStoryFree(storyId, checked);
        setSavingFree(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to update free flag" });
            return;
        }
        setGroup((prev) => {
            if (!prev) return prev;
            return { ...prev, storyJigsaw: { ...(prev.storyJigsaw || {}), free: checked } };
        });
    }, [storyId]);
    const storyJigsaw = group?.storyJigsaw ?? null;
    const title = getStoryTitle(story) || storyId || "—";
    const imageUrl = getImageUrl(story);
    const originalImageUrl = getOriginalImageUrl(story);
    const displayImageUrl = imageUrl && imageBust
        ? `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}v=${imageBust}`
        : imageUrl;
    const bundleState = String(storyJigsaw?.state || "").toUpperCase();
    const standalone = Boolean(story?.standaloneImage);
    const canPublish =
        sortedTemplates.length > 0 &&
        (bundleState === "REVIEWED" ||
            bundleState === "DISABLED" ||
            (standalone && (bundleState === "DRAFT" || bundleState === "REVIEWED")));
    const canDisable = bundleState === "PUBLISHED";
    const isDisabled = bundleState === "DISABLED";
    const isDiscarded = bundleState === "DISCARDED";
    const isUnavailable = isDisabled || isDiscarded;
    const publishedAt = storyJigsaw?.publishedAt ?? storyJigsaw?.published_at ?? null;

    const handleFacebookPost = async () => {
        if (facebookPosting) return;
        const url = originalImageUrl;
        if (!url || !url.startsWith("https://")) {
            setSnackbar({
                open: true,
                severity: "error",
                message: "The original image must be a public HTTPS URL so Facebook can fetch it.",
            });
            return;
        }
        setFacebookPosting(true);
        try {
            const result = await FacebookTestService.postImageUrl(url, facebookCaption(title, storyId), {
                published: true,
            });
            if (result && result.code === "FAILURE") {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.message || "Failed to post to Facebook",
                });
                return;
            }
            const postId = result?.postId || result?.graph?.post_id || "";
            const pageUrl = postId
                ? `https://www.facebook.com/${postId}`
                : "https://www.facebook.com/profile.php?id=61562830956867";
            window.open(pageUrl, "_blank", "noopener,noreferrer");
            setSnackbar({ open: true, severity: "success", message: "Posted the original image to Facebook." });
        } catch (err) {
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Failed to post to Facebook",
            });
        } finally {
            setFacebookPosting(false);
        }
    };

    const handlePublish = useCallback(async () => {
        if (!storyId) return;
        if (sortedTemplates.length === 0) {
            setSnackbar({ open: true, severity: "error", message: "No jigsaw templates to publish" });
            return;
        }
        setPublishing(true);
        const result = await AdminJigsawService.publishStory(storyId);
        setPublishing(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Publish failed" });
            return;
        }
        setSnackbar({ open: true, severity: "success", message: "Published successfully" });
        fetchGroup();
    }, [storyId, sortedTemplates.length, fetchGroup]);

    const handleConfirmDisable = useCallback(async () => {
        if (!storyId || disabling) return;
        setDisabling(true);
        const result = await AdminJigsawService.disableStory(storyId);
        setDisabling(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Disable failed" });
            return;
        }
        setSnackbar({
            open: true,
            severity: "success",
            message: "Puzzle disabled. Players cannot access it. It will be discarded in 1 week.",
        });
        fetchGroup({ silent: true });
    }, [storyId, disabling, fetchGroup]);

    return (
        <Box sx={{ p: 3, width: "100%", maxWidth: "100%" }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(detailBack.path)} sx={{ mb: 2 }}>
                {detailBack.label}
            </Button>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <Stack spacing={3}>
                    <Paper sx={{ p: 3 }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={2}
                            sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
                        >
                            <Typography variant="h5" sx={{ fontWeight: 600, flex: "1 1 auto", minWidth: 0 }}>
                                {title}
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleEnsureTemplates}
                                disabled={ensureSubmitting || !storyId}
                                startIcon={
                                    ensureSubmitting ? <CircularProgress size={16} color="inherit" /> : null
                                }
                                sx={{ flexShrink: 0, textTransform: "none" }}
                            >
                                {ensureSubmitting ? "Ensuring…" : "Ensure missing templates"}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setRegenDialogOpen(true)}
                                disabled={previewGenerating || !storyId}
                                startIcon={
                                    previewGenerating ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <AutorenewIcon />
                                    )
                                }
                                sx={{ flexShrink: 0, textTransform: "none" }}
                            >
                                {previewGenerating ? "Regenerating…" : "Re-generate image"}
                            </Button>
                            {canPublish ? (
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handlePublish}
                                    disabled={publishing || !storyId}
                                    startIcon={
                                        publishing ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <PublishIcon />
                                        )
                                    }
                                    sx={{
                                        flexShrink: 0,
                                        textTransform: "none",
                                        backgroundColor: "#3BA666",
                                        "&:hover": { backgroundColor: "#2e7d32" },
                                    }}
                                >
                                    {publishing ? "Publishing…" : "Publish"}
                                </Button>
                            ) : null}
                            {canDisable ? (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setDisableDialogOpen(true)}
                                    disabled={disabling || !storyId}
                                    startIcon={
                                        disabling ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <BlockIcon />
                                        )
                                    }
                                    sx={{ flexShrink: 0, textTransform: "none", color: "grey.700", borderColor: "grey.500" }}
                                >
                                    {disabling ? "Disabling…" : "Disable"}
                                </Button>
                            ) : null}
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={handleDeleteClick}
                                disabled={deleting || !storyId}
                                startIcon={
                                    deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
                                }
                                sx={{ flexShrink: 0, textTransform: "none" }}
                            >
                                {deleting ? "Deleting…" : "Delete"}
                            </Button>
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                            {storyJigsaw?.state && (
                                <Chip
                                    size="small"
                                    label={storyJigsaw.state}
                                    color={storyJigsaw.state === "PUBLISHED" ? "success" : "default"}
                                    sx={
                                        isUnavailable
                                            ? { bgcolor: "grey.400", color: "grey.900", fontWeight: 600 }
                                            : undefined
                                    }
                                />
                            )}
                            {storyJigsaw && (
                                <Chip
                                    size="small"
                                    label={storyJigsaw.free ? "Free" : "Paid"}
                                    color={storyJigsaw.free ? "success" : "default"}
                                    variant="outlined"
                                />
                            )}
                            {publishedAt ? (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Published ${new Date(publishedAt).toLocaleString()}`}
                                />
                            ) : null}
                            <Chip
                                size="small"
                                label={
                                    hideDisabled && disabledTemplateCount > 0
                                        ? `${templates.length} shown · ${disabledTemplateCount} disabled hidden`
                                        : `${templates.length} template${templates.length !== 1 ? "s" : ""}`
                                }
                                variant="outlined"
                            />
                            <Chip
                                size="small"
                                label={`Color baskets: ${templates.reduce(
                                    (sum, tpl) =>
                                        sum + (Number(tpl?.colorBasketCount) > 0 ? Number(tpl.colorBasketCount) : 0),
                                    0,
                                )}`}
                                variant="outlined"
                            />
                        </Stack>
                        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={storyJigsaw?.free === true}
                                        disabled={!storyId || savingFree || !storyJigsaw}
                                        onChange={(e) => handleFreeChange(e.target.checked)}
                                        inputProps={{ "aria-label": "Free" }}
                                    />
                                }
                                label="Free"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={hideDisabled}
                                        onChange={(e) => handleHideDisabledChange(e.target.checked)}
                                    />
                                }
                                label="Hide disabled templates"
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<OpenInNewIcon />}
                                onClick={() =>
                                    window.open(`/puzzle/play-jigsaw/${encodeURIComponent(storyId)}`, "_blank")
                                }
                            >
                                Play
                            </Button>
                            <Tooltip title="Check Facebook preview">
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label="Check Facebook preview"
                                        disabled={!originalImageUrl}
                                        onClick={() => setFacebookPreviewOpen(true)}
                                        sx={{ color: "#1877F2" }}
                                    >
                                        <FacebookIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {bedrockIdeaId ? (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    component={RouterLink}
                                    to={`/admin/bedrock-idea/edit/${encodeURIComponent(bedrockIdeaId)}`}
                                    startIcon={<AutoAwesomeIcon />}
                                    title={bedrockIdeaTitle || bedrockIdeaId}
                                    sx={{
                                        textTransform: "none",
                                        maxWidth: { xs: "100%", sm: 380 },
                                        "& .MuiButton-startIcon": { flexShrink: 0 },
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {bedrockIdeaTitle
                                            ? `Bedrock: ${bedrockIdeaTitle}`
                                            : "Bedrock idea"}
                                    </Box>
                                </Button>
                            ) : null}
                        </Stack>
                        {isUnavailable ? (
                            <Alert severity={isDiscarded ? "info" : "warning"} sx={{ mb: 2 }}>
                                {isDiscarded
                                    ? "This puzzle has been discarded and cannot be accessed by the app."
                                    : "Not available. Will be removed in 1 week, then discarded."}
                            </Alert>
                        ) : null}
                    </Paper>

                    {displayImageUrl ? (
                            <Paper variant="outlined" sx={{ p: 1.5, width: "100%", position: "relative" }}>
                                <Box sx={isUnavailable ? { filter: "grayscale(1)", opacity: 0.78 } : undefined}>
                                    <OriginalImageViewer imageUrl={displayImageUrl} alt={title} />
                                </Box>
                                {isUnavailable ? (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 12,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: "rgba(80,80,80,0.55)",
                                            borderRadius: 1,
                                            pointerEvents: "none",
                                        }}
                                    >
                                        <Typography variant="subtitle1" sx={{ color: "common.white", fontWeight: 700, textAlign: "center", px: 2 }}>
                                            {isDiscarded ? "Discarded" : "Not available — will be removed in 1 week"}
                                        </Typography>
                                    </Box>
                                ) : null}
                            </Paper>
                        ) : (
                            <Typography color="text.secondary">No original image.</Typography>
                        )}

                        <Accordion defaultExpanded={false} disableGutters>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="jigsaw-templates-content"
                                id="jigsaw-templates-header"
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Templates ({templates.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {templates.length === 0 ? (
                                    <Typography color="text.secondary">
                                        {sortedTemplates.length > 0 && hideDisabled
                                            ? "No enabled templates — turn off “Hide disabled templates” to view all rows."
                                            : "No templates."}
                                    </Typography>
                                ) : (
                                    <Stack spacing={1.25}>
                                {templates.map((tpl, idx) => {
                                    const isDisabledTemplate = !isJigsawTemplateEnabled(tpl);
                                    const colorBasketCount = Number(tpl?.colorBasketCount);
                                    const hasColorBaskets = Number.isFinite(colorBasketCount) && colorBasketCount > 0;
                                    const gridSize = tpl.tileSize || resolveJigsawTemplateTileSize(tpl);
                                    const name = tpl.templateName || tpl.name || `Template ${idx + 1}`;
                                    return (
                                        <Paper
                                            key={tpl.id || idx}
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                ...(isDisabledTemplate
                                                    ? { bgcolor: "action.hover", opacity: 0.88 }
                                                    : undefined),
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                justifyContent="space-between"
                                                flexWrap="wrap"
                                                useFlexGap
                                                rowGap={1}
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    flexWrap="wrap"
                                                    useFlexGap
                                                    sx={{ minWidth: 0, flex: "1 1 auto" }}
                                                >
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: isDisabledTemplate ? "text.disabled" : "text.primary",
                                                            fontStyle: isDisabledTemplate ? "italic" : "normal",
                                                        }}
                                                    >
                                                        {name}
                                                    </Typography>
                                                    {isDisabledTemplate ? (
                                                        <Chip size="small" label="Disabled" variant="outlined" />
                                                    ) : (
                                                        <Chip size="small" label="Exists" color="success" variant="outlined" />
                                                    )}
                                                    {gridSize ? (
                                                        <Chip size="small" label={`${gridSize}×${gridSize}`} />
                                                    ) : null}
                                                    {tpl.difficultyLevel ? (
                                                        <Chip
                                                            size="small"
                                                            label={
                                                                DIFF_LABEL[String(tpl.difficultyLevel).toUpperCase()] ??
                                                                tpl.difficultyLevel
                                                            }
                                                            variant="outlined"
                                                        />
                                                    ) : null}
                                                    <Chip
                                                        size="small"
                                                        clickable={Boolean(tpl.id)}
                                                        component={tpl.id ? "button" : "div"}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!tpl.id || !storyId) return;
                                                            navigate(
                                                                `/admin/jigsaw/browse/${encodeURIComponent(storyId)}/color-baskets/${encodeURIComponent(tpl.id)}`,
                                                                { state: location.state },
                                                            );
                                                        }}
                                                        label={
                                                            hasColorBaskets
                                                                ? `Color baskets: ${colorBasketCount}`
                                                                : "Color baskets: 0"
                                                        }
                                                        color={hasColorBaskets ? "success" : "default"}
                                                        variant="outlined"
                                                        sx={tpl.id ? { cursor: "pointer" } : undefined}
                                                    />
                                                </Stack>
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Preview ${name}`}
                                                    onClick={() => openCardPreview(tpl)}
                                                >
                                                    <ImageIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </Paper>
                                    );
                                })}
                                    </Stack>
                                )}
                            </AccordionDetails>
                        </Accordion>

                    {storyId ? (
                        <Paper sx={{ p: 2, width: "100%", overflow: "hidden" }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                                Play logs
                            </Typography>
                            <AdminJigsawPlay storyId={storyId} embedded />
                        </Paper>
                    ) : null}
                </Stack>
            )}

            <Dialog open={previewDialogOpen} onClose={closePreviewDialog} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Jigsaw previews
                    {Array.isArray(previewResult?.items) && previewResult.items.length > 0
                        ? ` (${previewResult.items.length})`
                        : ""}
                </DialogTitle>
                <DialogContent dividers>
                    {previewResult?.error ? (
                        <Typography color="error">{previewResult.error}</Typography>
                    ) : Array.isArray(previewResult?.items) && previewResult.items.length > 0 ? (
                        <Stack spacing={2}>
                            {previewResult.items.map((item) => {
                                const levelKey = String(item.difficultyLevel || "").toUpperCase();
                                const levelLabel = DIFF_LABEL[levelKey] ?? (levelKey || "—");
                                const ts = item.tileSize;
                                const heading =
                                    ts != null && ts > 0 ? `${ts}×${ts} — ${levelLabel}` : levelLabel;
                                return (
                                    <Paper
                                        key={item.jigsawId || item.s3Key || heading}
                                        variant="outlined"
                                        sx={{ p: 2 }}
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                            {heading}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                display: "block",
                                                fontFamily: "monospace",
                                                wordBreak: "break-all",
                                                mb: 1,
                                            }}
                                        >
                                            jigsawId: {item.jigsawId || "—"}
                                        </Typography>
                                        {item.previewUrl ? (
                                            <Box
                                                component="img"
                                                src={item.previewUrl}
                                                alt={heading}
                                                sx={{
                                                    width: "100%",
                                                    maxHeight: 360,
                                                    objectFit: "contain",
                                                    display: "block",
                                                    borderRadius: 1,
                                                    bgcolor: "action.hover",
                                                    mb: 1,
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                No preview URL
                                            </Typography>
                                        )}
                                        {item.s3Key ? (
                                            <Typography
                                                variant="body2"
                                                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                                            >
                                                <Box component="span" sx={{ fontWeight: 600 }}>
                                                    s3Key:
                                                </Box>{" "}
                                                {item.s3Key}
                                            </Typography>
                                        ) : null}
                                        {item.previewUrl ? (
                                            <Button
                                                component="a"
                                                href={item.previewUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                size="small"
                                                sx={{ mt: 1, textTransform: "none" }}
                                            >
                                                Open in new tab
                                            </Button>
                                        ) : null}
                                    </Paper>
                                );
                            })}
                        </Stack>
                    ) : (
                        <Typography color="text.secondary">No preview items returned.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePreviewDialog}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={facebookPreviewOpen}
                onClose={() => setFacebookPreviewOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Facebook preview</DialogTitle>
                <DialogContent dividers>
                    {originalImageUrl ? (
                        <Box
                            component="img"
                            src={originalImageUrl}
                            alt={title}
                            sx={{
                                width: "100%",
                                height: "auto",
                                borderRadius: 1,
                                display: "block",
                                mb: 2,
                            }}
                        />
                    ) : (
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                            This story has no original image to share.
                        </Typography>
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 600, whiteSpace: "pre-wrap" }}>
                        {facebookCaption(title, storyId)}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFacebookPreviewOpen(false)} disabled={facebookPosting}>
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!originalImageUrl || facebookPosting}
                        onClick={handleFacebookPost}
                        sx={{ textTransform: "none", backgroundColor: "#1877F2", "&:hover": { backgroundColor: "#166fe0" } }}
                    >
                        {facebookPosting ? "Posting…" : "Post"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={cardPreview.open} onClose={closeCardPreview} maxWidth="md" fullWidth>
                <DialogTitle>{cardPreview.title} — Preview</DialogTitle>
                <DialogContent dividers sx={{ textAlign: "center" }}>
                    {cardPreview.url ? (
                        <Box
                            component="img"
                            src={cardPreview.url}
                            alt={`${cardPreview.title} preview`}
                            sx={{
                                maxWidth: "100%",
                                height: "auto",
                                borderRadius: 1,
                                display: "block",
                                mx: "auto",
                            }}
                        />
                    ) : (
                        <Typography color="text.secondary">
                            No preview URL for this template. Use <strong>Re-generate image</strong> above.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {cardPreview.url ? (
                        <Button
                            component="a"
                            href={cardPreview.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<OpenInNewIcon />}
                            sx={{ textTransform: "none" }}
                        >
                            Open in new tab
                        </Button>
                    ) : null}
                    <Button onClick={closeCardPreview}>Close</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={disableDialogOpen}
                setOpen={setDisableDialogOpen}
                title="Disable this puzzle?"
                message="Players will not be able to open or continue this puzzle. It stays disabled for 1 week, then it is discarded."
                targetName={title ? `'${title}'` : storyId ? `Story ${storyId}` : "This jigsaw"}
                targetId={storyId}
                callback={handleConfirmDisable}
                warningAlert="Not available. Will be removed in 1 week."
                okText="Disable"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={regenDialogOpen}
                setOpen={setRegenDialogOpen}
                title="Regenerate image?"
                message="This replaces the current illustration and regenerates jigsaw puzzle images. Story text is not changed."
                targetName={title ? `'${title}'` : storyId ? `Story ${storyId}` : "This jigsaw"}
                targetId={storyId}
                callback={handleConfirmRegenImage}
                warningAlert="Would you like to re-generate this image? Did you check the players who played this puzzle."
                okText="OK"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={confirmOpen}
                setOpen={setConfirmOpen}
                title="Delete jigsaw"
                targetName={title}
                targetId={storyId}
                message={[
                    "This permanently deletes the story record, the story image, all jigsaw templates, and the related sliding puzzle.",
                    "Facebook and Instagram posts of this image are removed when post ids are known.",
                    "The crossword or word-search puzzle is not deleted. Scheduled jobs will not recreate this story or its sliding/jigsaw puzzles.",
                    "This action cannot be undone.",
                ]}
                callback={(id) => handleDelete(id)}
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
